import { produce } from 'immer';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Notice } from '@/core';

export interface VizPanelColor {
  /**
   * Text color.
   */
  color?: string;
  /**
   * Background color.
   */
  background?: string;
}

type VizPanelMenuItem = {
  /**
   * Unique key for the menu item.
   */
  key: string;
  /**
   * Label for the menu item.
   */
  label: string;
  /**
   * Callback function to execute when the menu item is clicked.
   */
  onClick: () => void;
};

export interface VizPanelContextValue {
  /**
   * Color values used to render the `VizPanel`.
   */
  color: VizPanelColor;
  /**
   * Set the color values used when rendering the `VizPanel`.
   */
  setColor: (color: VizPanelColor) => void;
  /**
   * Notices for the `VizPanel` that are managed from within the visualization.
   *
   * Note that the `VizPanel` can also render notices from queries configured using `DataQueriesProvider`.
   * These are managed by the associated data query plugins and are not included in this array.
   */
  panelNotices: Notice[];

  /**
   * Map of notice keys to notices for the `VizPanel`.
   */
  panelNoticesMap: Readonly<Record<string, Notice>>;

  /**
   * Removes all notices for the `VizPanel` that are managed from within the visualization.
   *
   * Note that this does not impact notices from queries.
   */
  clearPanelNotices: () => void;
  /**
   * Set a new notice for the `VizPanel`. Useful for surfacing notices to the user from within a visualization.
   */
  setPanelNotice: (key: string, newValue: Notice) => void;
  /**
   * Delete a specific notice from the `VizPanel`.
   */
  deletePanelNotice: (key: string) => void;

  /**
   * Map of menu item keys to menu items for the `VizPanel`.
   */
  menuItemsMap: Readonly<Record<string, VizPanelMenuItem>>;
  /**
   * Set a new menu item for the `VizPanel`.
   */
  setMenuItem: (key: string, newMenuItem: Omit<VizPanelMenuItem, 'key'>) => void;
  /**
   * Delete a specific menu item from the `VizPanel`.
   */
  deleteMenuItem: (key: string) => void;
  /**
   * Returns an array of menu items for the `VizPanel`.
   */
  menuItems: VizPanelMenuItem[];

  /**
   * Panel key used as a unique identifier for the `VizPanel`.
   */
  panelKey: string;
}

// Empty context we use for initial configuration and in case the context is not set. This makes working with the
// context and types a little easier to manage.
const EMPTY_VIZ_PANEL_CONTEXT: VizPanelContextValue = {
  panelKey: 'temp-panel-key',
  color: {},
  setColor: () => {},
  panelNotices: [],
  panelNoticesMap: {},
  clearPanelNotices: () => {},
  setPanelNotice: () => {},
  deletePanelNotice: () => {},
  menuItemsMap: {},
  menuItems: [],
  setMenuItem: () => {},
  deleteMenuItem: () => {},
};

const VizPanelContext = createContext<VizPanelContextValue>(EMPTY_VIZ_PANEL_CONTEXT);

export interface VizPanelContextProviderProps {
  children: React.ReactNode;
  panelKey: string;
}

/**
 * Context provider that helps manage VizPanel state handling between the parent `VizPanel` and
 * the data visualizations that live inside it.
 */
export function VizPanelContextProvider({ children, panelKey }: VizPanelContextProviderProps) {
  const [panelNoticesMap, setPanelNoticesMap] = useState<Record<string, Notice>>({});
  const [color, setColor] = useState<VizPanelColor>({});

  const clearPanelNotices = useCallback(() => {
    setPanelNoticesMap({});
  }, []);

  const setPanelNotice = useCallback((key: string, newValue: Notice) => {
    setPanelNoticesMap(
      produce((draft) => {
        draft[key] = newValue;
      })
    );
  }, []);

  const deletePanelNotice = useCallback((key: string) => {
    setPanelNoticesMap(
      produce((draft) => {
        delete draft[key];
      })
    );
  }, []);

  const panelNotices: Notice[] = useMemo(() => {
    // The notices are stored as a key-value for ease of setting/deleting items. Transform into an
    // array of notices for usage.
    return Object.values(panelNoticesMap);
  }, [panelNoticesMap]);

  const [menuItemsMap, setMenuItemsMap] = useState<Record<string, VizPanelMenuItem>>({});

  const setMenuItem = useCallback((key: string, newMenuItem: Omit<VizPanelMenuItem, 'key'>) => {
    setMenuItemsMap(
      produce((draft) => {
        draft[key] = { ...newMenuItem, key };
      })
    );
  }, []);

  const menuItems: VizPanelMenuItem[] = useMemo(() => {
    // The menu items are stored as a key-value for ease of setting/deleting items. Transform into an
    // array of menu items for usage.
    return Object.values(menuItemsMap);
  }, [menuItemsMap]);

  const deleteMenuItem = useCallback((key: string) => {
    setMenuItemsMap(
      produce((draft) => {
        delete draft[key];
      })
    );
  }, []);

  const value: VizPanelContextValue = useMemo(() => {
    return {
      color,
      setColor,
      panelNotices,
      panelNoticesMap,
      clearPanelNotices,
      setPanelNotice,
      deletePanelNotice,
      menuItems,
      menuItemsMap,
      setMenuItem,
      deleteMenuItem,
      panelKey,
    };
  }, [
    clearPanelNotices,
    color,
    deletePanelNotice,
    panelNotices,
    panelNoticesMap,
    setPanelNotice,
    menuItems,
    menuItemsMap,
    setMenuItem,
    deleteMenuItem,
    panelKey,
  ]);

  return <VizPanelContext.Provider value={value}>{children}</VizPanelContext.Provider>;
}

/**
 * @private Internal context hook for VizPanel state.
 */
const useVizPanelContext = () => {
  const context = useContext(VizPanelContext);
  return context ?? EMPTY_VIZ_PANEL_CONTEXT;
};

type UseVizPanelColorResponse = Pick<VizPanelContextValue, 'color' | 'setColor'>;

/**
 * Hook that returns the `VizPanel` color and related utility functions.
 */
export const useVizPanelColor = (): UseVizPanelColorResponse => {
  const { color, setColor } = useVizPanelContext();

  return {
    color,
    setColor,
  };
};

type UseVizPanelNoticesResponse = Pick<
  VizPanelContextValue,
  'panelNotices' | 'panelNoticesMap' | 'setPanelNotice' | 'deletePanelNotice' | 'clearPanelNotices'
>;

/**
 * Returns notices for the `VizPanel` that are managed within the visualization and helper functions to modify them.
 * Useful for surfacing notices to the user from within a visualization.
 *
 * Note that this hook and helper functions only include notices internal to the `VizPanel`. The `VizPanel` can also
 * render notices from queries configured using `DataQueriesProvider`. These are managed by the associated data query
 * plugins.
 */
export const useVizPanelNotices = (): UseVizPanelNoticesResponse => {
  const { panelNotices, panelNoticesMap, setPanelNotice, deletePanelNotice, clearPanelNotices } = useVizPanelContext();

  return {
    panelNotices,
    panelNoticesMap,
    setPanelNotice,
    deletePanelNotice,
    clearPanelNotices,
  };
};

type UseVizPanelNoticeResponse = {
  panelNotice?: Notice;
  setPanelNotice: (newValue: Notice) => void;
  deletePanelNotice: () => void;
};

/**
 * Hook for managing a single notice for the `VizPanel`. Takes a `noticeKey` that is the unique identifier for that
 * notice, and returns the value of that notice and utilties for setting and deleting that notice.
 */
export const useVizPanelNotice = (noticeKey: string): UseVizPanelNoticeResponse => {
  const {
    panelNoticesMap,
    setPanelNotice: initSetPanelNotice,
    deletePanelNotice: initDeletePanelNotice,
  } = useVizPanelContext();

  const setPanelNotice = useCallback(
    (newValue: Notice) => {
      return initSetPanelNotice(noticeKey, newValue);
    },
    [initSetPanelNotice, noticeKey]
  );

  const deletePanelNotice = useCallback(() => {
    return initDeletePanelNotice(noticeKey);
  }, [initDeletePanelNotice, noticeKey]);

  return {
    panelNotice: panelNoticesMap[noticeKey],
    setPanelNotice,
    deletePanelNotice,
  };
};

export function useVizPanelMenuItems() {
  const { menuItemsMap } = useVizPanelContext();
  return useMemo(() => {
    return Object.entries(menuItemsMap).map(([key, menuItem]) => ({
      ...menuItem,
      key,
    }));
  }, [menuItemsMap]);
}

export const useVizPanelMenuItem = (menuItemKey: string) => {
  const { setMenuItem: initSetMenuItem, deleteMenuItem: initDeletePanelNotice } = useVizPanelContext();

  const setMenuItem = useCallback(
    (newMenuItem: Omit<VizPanelMenuItem, 'key'>) => {
      return initSetMenuItem(menuItemKey, newMenuItem);
    },
    [initSetMenuItem, menuItemKey]
  );
  const deleteMenuItem = useCallback(() => {
    return initDeletePanelNotice(menuItemKey);
  }, [initDeletePanelNotice, menuItemKey]);
  return {
    setMenuItem,
    deleteMenuItem,
  };
};

/**
 * Returns the unique identifier (panelKey) for the `VizPanel`.
 */
export const usePanelKey = () => {
  const { panelKey } = useVizPanelContext();
  return panelKey;
};

/**
 * Returns true when `VizPanelContext` is present, which indicates the code is running inside a
 * `VizPanel` or a similar component (e.g. we intend to introduces a `VizContainer` component in
 * the near future to support composition that will share many commonalities with `VizPanel`).
 */
export const useIsInVizPanel = (): boolean => {
  const context = useVizPanelContext();

  // The EMPTY_VIZ_PANEL_CONTEXT is only used when the context has not been set up with `VizPanelContextProvider`.
  return context !== EMPTY_VIZ_PANEL_CONTEXT;
};
